import * as React from 'react';
import { forTablet } from '@westwing/ui-kit/breakPoints';
import template from '@westwing/ui-kit/template';
import styled from 'styled-components';
import Text from '@ids/components/Typography/Text';
import { usePriceTiersBlock } from '@ids/context';
import Display from '@ids/components/Layout/Display';
import { useIdsGaTracking } from '@ids/hooks';
import { useAppshellMeta } from '@ids/appshell/hooks';

const StyledBusinessContact = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.charcoal20New};
    width: min(1015px, 100%);
    padding: 20px;
    margin: 0 var(--section-x-space-mobile);

    ${forTablet`
        padding: 20px 40px;
        margin-bottom: 48px;
        display: grid;
        grid-template-columns: 1fr 250px;
        align-items: center;
        grid-gap: 4vmax;
    `}
`;

const StyledText = styled.div``;

const StyledTitle = styled(Text).attrs({ variant: 'subtitle' })`
    margin-bottom: 20px;

    ${forTablet`
        margin-bottom: 10px;
    `}
`;

const StyledDescription = styled(Text).attrs({ variant: 'paragraph' })``;
const StyledFallbackMessage = styled(Text).attrs({ variant: 'paragraph' })``;

const StyledContactUsLink = styled.a`
    display: grid;
    place-items: center;
    text-decoration: none;
    color: ${({ theme }) => theme.colors.charcoal100};
    border: 1px solid currentColor;
    width: 100%;
    height: 52px;
`;

const AppBusinessContact = () => {
    const priceTiersBlock = usePriceTiersBlock();
    const { businessContactTitle, businessContactFallbackMessage } = priceTiersBlock ?? {};

    if (!businessContactFallbackMessage) {
        return null;
    }

    return (
        <React.Fragment>
            <StyledTitle>{businessContactTitle}</StyledTitle>
            <StyledFallbackMessage
                data-testid="price-tiers-business-contact-fallback-message"
                dangerouslySetInnerHTML={{ __html: template(businessContactFallbackMessage) }}
            />
        </React.Fragment>
    );
};

const WebBusinessContact = () => {
    const priceTiersBlock = usePriceTiersBlock();
    const { trackIdsEvent } = useIdsGaTracking();

    const { businessContactTitle, businessContactDescription, businessContactCtaText, businessContactLink } =
        priceTiersBlock ?? {};

    const onClick = () => trackIdsEvent({ action: 'Price Tiers', label: 'CTA Studio Business' });

    return (
        <React.Fragment>
            <StyledText>
                <StyledTitle>{businessContactTitle}</StyledTitle>
                <Display.FromTablet>
                    <StyledDescription>{businessContactDescription}</StyledDescription>
                </Display.FromTablet>
            </StyledText>
            <StyledContactUsLink
                href={businessContactLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="price-tiers-business-contact-cta"
                onClick={onClick}
            >
                {businessContactCtaText}
            </StyledContactUsLink>
        </React.Fragment>
    );
};

const BusinessContact = () => {
    const priceTiersBlock = usePriceTiersBlock();
    const { isApp } = useAppshellMeta();

    const { businessContactTitle, businessContactCtaText } = priceTiersBlock ?? {};

    if (!businessContactTitle || !businessContactCtaText) {
        return null;
    }

    return (
        <StyledBusinessContact data-testid="price-tiers-business-contact">
            {isApp ? <AppBusinessContact /> : <WebBusinessContact />}
        </StyledBusinessContact>
    );
};

export default BusinessContact;
